import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Card } from "@onemileauto/ui";
import { useMailbox } from "../../hooks/use-mailbox";
import { AccountsPanel } from "../../components/admin/accounts-panel";
import { MailboxAclPanel } from "../../components/admin/mailbox-acl-panel";
import { MailboxSettingsPanel } from "../../components/admin/mailbox-settings-panel";
import { ProvisionMailboxForm } from "../../components/admin/provision-mailbox-form";

/**
 * Owner-only admin surface (Phase 5: staff mailbox delegation; Phase 9:
 * global accounts view + per-mailbox signature/away-message) — provision
 * new @onemileauto.com mailboxes, see every account in the system and what
 * it can reach, and manage who/what each owned mailbox does. A real page
 * (not a modal), reachable via the gear icon in the folder rail/drawer (see
 * mail-shell.tsx) — only shown there at all once a user actually owns a
 * mailbox.
 *
 * The real access gate is server-side (routes/admin.ts's requireSystemOwner,
 * routes/permissions.ts's requireMailbox("admin"), routes/mailboxes.ts's
 * settings route using the same check) — this client-side check is purely
 * so a non-owner who somehow lands on /admin sees a plain explanation
 * instead of a broken/empty page or a raw 404 from the API.
 */
export function AdminPage() {
  const { mailboxes, isLoading, isOwnerOfAny } = useMailbox();
  const ownedMailboxes = mailboxes.filter((m) => m.permissionLevel === "owner");

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 overflow-y-auto p-4">
      <Link to="/" className="flex w-fit items-center gap-1.5 text-sm font-medium text-muted hover:text-charcoal">
        <ArrowLeft size={16} />
        Back to mail
      </Link>
      <h1 className="font-display text-2xl text-charcoal">Manage mailboxes</h1>

      {isLoading ? (
        <p className="text-sm text-muted">Loading&hellip;</p>
      ) : !isOwnerOfAny ? (
        <Card className="p-6 text-center">
          <p className="font-display text-lg text-charcoal">This page is for mailbox owners</p>
          <p className="mt-2 text-sm text-muted">You don&rsquo;t own any mailboxes, so there&rsquo;s nothing to manage here.</p>
        </Card>
      ) : (
        <>
          <AccountsPanel />

          {ownedMailboxes.map((mailbox) => (
            <Card key={mailbox.id} className="overflow-hidden p-0">
              <div className="px-4 py-4">
                <h2 className="font-display text-lg text-charcoal">{mailbox.displayName}</h2>
                <p className="text-sm text-muted">{mailbox.address}</p>
              </div>
              <MailboxSettingsPanel mailbox={mailbox} />
              <MailboxAclPanel mailboxId={mailbox.id} mailboxAddress={mailbox.address} />
            </Card>
          ))}

          <ProvisionMailboxForm />
        </>
      )}
    </div>
  );
}
